import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUserContext } from '../../../user/UserContext';
import { useLoading } from '../../../loading/LoadingContext';
import { AccountIntegration, Listing__c, Listing__c_Missing, ListingAdjustment, ListingsInventory, ListingsOrderItem, ListingsProduct, PicklistValueInfo } from '../../utilities/listings.interfaces';
import CustomError, { CustomErrorProps } from '../../../common/components/CustomError';
import { DateTime } from 'luxon';
import { Form } from 'react-bootstrap';
import { apiFetch } from '../../../utility/fetch';
import { getListingAdjustmentsByProductId, getListingInventoryByProductId, getListingOrderItemsByProductId, getListingsByIntegrationIdSOQL, getListingsByProductIdSOQL, getPicklistValueStatusesByFieldNameSOQL, LISTINGS_ACCOUNT_INTEGRATIONS_SOQL, LISTINGS_PRODUCTS_SOQL } from '../../utilities/listings.queries';
import { SalesforceQueryResponse } from '../../../orders/orders.interfaces';
import { SALESFORCE_URLS_BY_ENVIRONMENT } from '../../../common/common.entities';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { CalendarPlus, Exclamation, SlashCircle } from 'react-bootstrap-icons';
import ListingsInventoryModal from '../lookups/ListingsInventoryModal';
import ListingsOrderItemsModal from '../lookups/ListingsOrderItemsModal';
import ListingsAdjustmentsModal from '../lookups/ListingsAdjustmentsModal';
import { API_DEV_URL, API_PROD_URL } from '../../..';
import { determineListingsToCreate, isPendingEnable } from '../../utilities/listings.helpers';
import RefreshSvg from '../../../common/components/svg/RefreshSvg';
import AccountsTable from '../../../accounts/AccountsTable';
import { getTryCatchError } from '../../../common/common.helpers';
import ProductListingsModal from '../lookups/ProductListingsModal';
import { ListingsCreatedView } from '../views/ListingsCreatedView';
import { ListingsMissingView } from '../views/ListingsMissingView';
import { ListingsPendingEnableView } from '../views/ListingsPendingEnableView';

interface ListingsByAccountProps {
  environment: string
}

export default function ListingsByAccount(props: ListingsByAccountProps){
  const {userData} = useUserContext()

  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const [error, setError]: [CustomErrorProps, any] = useState({visible: false})
  const [refresh, setRefresh] = useState(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))

  const [selectedAccountIntegration, setSelectedAccountIntegration]: [AccountIntegration, any] = useState(undefined)
  
  const [listings, setListings]: [Listing__c[], any] = useState([])
  const [missing, setMissing]: [Listing__c_Missing[], any] = useState([])
  const [pending, setPending]: [Listing__c[], any] = useState([])
  
  const [accountIntegrations, setAccountIntegrations]: [AccountIntegration[], any] = useState([])
  const [inventoryStatuses, setInventoryStatuses]: [PicklistValueInfo[], any] = useState([])
  const [delistedReasons, setDelistedReasons]: [PicklistValueInfo[], any] = useState([])

  const [createdMarketingStatuses, setCreatedMarketingStatuses]: [string[], any] = useState([])
  const [missingMarketingStatuses, setMissingMarketingStatuses]: [string[], any] = useState([])

  const [inventoryModal, setInventoryModal]: [{visible?: boolean, account?: string, inventories?: ListingsInventory[], inventoryMapped?: string, mappingWarehouses?: string[], closeModal?: any, title?: string}, any] = useState({visible: false})
  const [orderItemsModal, setOrderItemsModal]: [{visible?: boolean, orderItems?: ListingsOrderItem[], closeModal?: any, title?: string}, any] = useState({visible: false})
  const [adjustmentsModal, setAdjustmentsModal]: [{visible?: boolean, adjustments?: ListingAdjustment[], closeModal?: any, title?: string, mpn?: string}, any] = useState({visible: false})
  const [listingsModal, setListingsModal]: [{visible?: boolean, listings?: Listing__c[], closeModal?: any, title?: string}, any] = useState({visible: false})

  function reloadData(){
    setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  }

  // #region LOADING
  const initialLoad = async function(){
    setError({visible: false})
    changeStep('Gathering Accounts')
    changeSubStep('Getting Accounts & Integrations from Mission Control')
    toggleLoading(true)
    try {
      const response = await apiFetch<SalesforceQueryResponse<AccountIntegration>>({
        endpoint: 'salesforceQuery',
        method: 'POST',
        accessToken: userData.accessToken,
        baseUrlOverride: props.environment === 'production' ? API_PROD_URL : API_DEV_URL,
        body: JSON.stringify({
          soql: LISTINGS_ACCOUNT_INTEGRATIONS_SOQL
        })
      })
      if(!response.ok){
        setError({
          closeError: () => {setError({visible: false})},
          content: JSON.stringify(response.body, null, 2),
          description: `Failed to get Account Integrations from Mission Control.`,
          title: 'Account Integrations Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
      setAccountIntegrations(response.body.records)
      changeSubStep('Getting Inventory Statuses')
      const inventoryResponse = await apiFetch<SalesforceQueryResponse<PicklistValueInfo>>({
        endpoint: 'salesforceQuery',
        method: 'POST',
        accessToken: userData.accessToken,
        baseUrlOverride: props.environment === 'production' ? API_PROD_URL : API_DEV_URL,
        body: JSON.stringify({
          soql: getPicklistValueStatusesByFieldNameSOQL('Inventory_Status__c')
        })
      })
      if(!inventoryResponse.ok){
        setError({
          closeError: () => {setError({visible: false})},
          content: JSON.stringify(inventoryResponse.body, null, 2),
          description: `Failed to get Inventory Statuses from Mission Control.`,
          title: 'Inventory Status Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
      setInventoryStatuses(inventoryResponse.body.records)
      changeSubStep('Getting Delisted Reasons')
      const delistedResponse = await apiFetch<SalesforceQueryResponse<PicklistValueInfo>>({
        endpoint: 'salesforceQuery',
        method: 'POST',
        accessToken: userData.accessToken,
        baseUrlOverride: props.environment === 'production' ? API_PROD_URL : API_DEV_URL,
        body: JSON.stringify({
          soql: getPicklistValueStatusesByFieldNameSOQL('Delisted_Reason__c')
        })
      })
      if(!delistedResponse.ok){
        setError({
          closeError: () => {setError({visible: false})},
          content: JSON.stringify(delistedResponse.body, null, 2),
          description: `Failed to get Delisted Reasons from Mission Control.`,
          title: 'Delisted Reason Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
      setDelistedReasons(delistedResponse.body.records)
    }catch(error){
      setError({
        closeError: () => {setError({visible: false})},
        content: error,
        description: `Failed to get data from Mission Control.`,
        title: 'Data Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
    }
    toggleLoading(false)
  }

  useEffect(() => {
    initialLoad();
  }, [props.environment, userData.accessToken, refresh]);

  const loadListings = async function(accountIntegration: AccountIntegration){
    setError({visible: false})

    if(!accountIntegration) return

    changeStep('Gathering Listings')
    changeSubStep(`Getting Listings from Mission Control for ${accountIntegration.Name}`)
    toggleLoading(true)
    try {
      const listingsResponse = await apiFetch<SalesforceQueryResponse<Listing__c>>({
        endpoint: 'salesforceQuery',
        method: 'POST',
        accessToken: userData.accessToken,
        baseUrlOverride: props.environment === 'production' ? API_PROD_URL : API_DEV_URL,
        body: JSON.stringify({
          soql: getListingsByIntegrationIdSOQL(accountIntegration.Order_Integration__c)
        })
      })
      if(!listingsResponse.ok){
        setError({
          closeError: () => {setError({visible: false})},
          content: JSON.stringify(listingsResponse.body, null, 2),
          description: `Failed to get Listings from Mission Control.`,
          title: 'Listings Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
      let index = 1
      const marketingStatuses = []
      for(const listing of listingsResponse.body.records){
        listing.index = index
        if(listing.Product__r.Marketing_Status__c && !marketingStatuses.includes(listing.Product__r.Marketing_Status__c)){
          marketingStatuses.push(listing.Product__r.Marketing_Status__c)
        }
        index += 1
      }
      setCreatedMarketingStatuses(marketingStatuses)

      changeSubStep(`Getting products to check for missing listings in ${accountIntegration.Name}`)
      const productResponse = await apiFetch<SalesforceQueryResponse<ListingsProduct>>({
        endpoint: 'salesforceQuery',
        method: 'POST',
        accessToken: userData.accessToken,
        baseUrlOverride: props.environment === 'production' ? API_PROD_URL : API_DEV_URL,
        body: JSON.stringify({
          soql: LISTINGS_PRODUCTS_SOQL
        })
      })
      if(!productResponse.ok){
        setError({
          closeError: () => {setError({visible: false})},
          content: JSON.stringify(productResponse.body, null, 2),
          description: `Failed to get Products from Mission Control.`,
          title: 'Products Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
      
      changeSubStep(`Determining missing listings for ${accountIntegration.Name}`)

      const missing = determineListingsToCreate(productResponse.body.records, selectedAccountIntegration, listingsResponse.body.records)
      const missingMarketingStatuses = []
      for(const listing of missing){
        if(listing.temp.Product.Marketing_Status__c && !missingMarketingStatuses.includes(listing.temp.Product.Marketing_Status__c)){
          missingMarketingStatuses.push(listing.temp.Product.Marketing_Status__c)
        }
        index += 1
      }
      setMissingMarketingStatuses(missingMarketingStatuses)

      changeSubStep(`Determining pending enable listings for ${accountIntegration.Name}`)
      const pendingListings = []
      let pendingIndex = 1
      const cutOffDate = DateTime.fromISO('2024-11-01T00:00:00.000-06:00')
      for(const listing of listingsResponse.body.records){
        if(listing.Enable__c === false && listing.Product__r.Marketing_Status__c === '4: Live' && !listing.Name.includes('-FBA')){
          if(DateTime.fromISO(listing.CreatedDate) <= cutOffDate) continue
          const clone = JSON.parse(JSON.stringify(listing))
          clone.invalids = isPendingEnable(clone)
          clone.index = pendingIndex
          pendingListings.push(clone)
          pendingIndex += 1
        }
      }
      setPending(pendingListings.sort((a, b) => 
        DateTime.fromISO(b.Product__r?.CreatedDate)?.toMillis() -
        DateTime.fromISO(a.Product__r?.CreatedDate)?.toMillis()
      ))
      setMissing(missing.sort((a, b) => 
        DateTime.fromISO(b.temp?.Product?.CreatedDate)?.toMillis() -
        DateTime.fromISO(a.temp?.Product?.CreatedDate)?.toMillis()
      ))
      setListings(listingsResponse.body.records)
      
    }catch(error){
      setError({
        closeError: () => {setError({visible: false})},
        content: JSON.stringify(getTryCatchError(error)),
        description: `Failed to get data from Mission Control.`,
        title: 'Data Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
    }
    toggleLoading(false)
  }

  useEffect(() => {
    if (selectedAccountIntegration) {
      loadListings(selectedAccountIntegration);
    }
  }, [selectedAccountIntegration, refresh]);

  // #region ACCOUNTS
  const handleAccountSelected = useCallback((e) => {
    const ai = accountIntegrations?.find(ai => ai.Id === e.target.value)
    setSelectedAccountIntegration(ai)
  }, [accountIntegrations])

  const accountsView = useMemo(() => {
    return <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px'}}>
      <div style={{width: '25%'}} className='beacon-card'>
        <span className='beacon-header'>ACCOUNT</span>
        <Form.Select onChange={handleAccountSelected}>
          <option selected disabled>--SELECT AN ACCOUNT--</option>
          {accountIntegrations?.map(ai => (
            <option id={ai.Id} value={ai.Id} data-record={ai}>{ai.Name}</option>
          ))}
        </Form.Select>
        <span title='Refresh listings for the currently selected account.' style={{cursor: 'pointer'}} onClick={reloadData}><RefreshSvg height={25}/></span>
      </div>
      {selectedAccountIntegration && <div style={{width: '35%', textAlign: 'center'}} className='beacon-card'>
          <span title='Opens the Account in Mission Control.' className='beacon-header'>
            <a href={`${SALESFORCE_URLS_BY_ENVIRONMENT[props.environment]}/${selectedAccountIntegration.Id}`} className='beacon-link' target="_blank" rel="noopener noreferrer">{selectedAccountIntegration.Name}</a>
          </span>
          <br/>
          <span title='Opens the Integration in Mission Control.' className='beacon-header'>
            <a href={`${SALESFORCE_URLS_BY_ENVIRONMENT[props.environment]}/${selectedAccountIntegration.Order_Integration__c}`} className='beacon-link' target="_blank" rel="noopener noreferrer">{selectedAccountIntegration.Order_Integration__r.Name}</a>
          </span>
          {selectedAccountIntegration.Third_Party_Ship_Billing__c === true && <>
            <br/>
            <span className='beacon-label'>
              THIRD PARTY SHIP BILLING
            </span>
          </>}
          <br/>
          <span className='beacon-label'>Auto-Create Listings is {selectedAccountIntegration.Order_Integration__r.Autocreate__c === true ? '' : 'not '} enabled.</span>
          <br/>
          <span className='beacon-label'>Auto-Enable Listings is {selectedAccountIntegration.Order_Integration__r.Auto_Enable_Listings__c === true ? '' : 'not '} enabled.</span>

        </div>  
      }
      {selectedAccountIntegration && <div style={{width: '35%'}} className='beacon-card'>
          <span className='beacon-label'><span className='beacon-header'>{listings.length.toLocaleString()}</span> total listing(s) in the 'Sunnydaze Decor', 'CASL Brands', 'WaterLev', and 'TankTop Covers' brands.</span>
          <br/>
          <span className='beacon-label'><span className='beacon-header'>{listings.filter(l => l.Enable__c === true).length.toLocaleString()}</span> are marketplace enabled. <span className='beacon-header'>{listings.filter(l => l.Enable__c === false).length.toLocaleString()}</span> are marketplace disabled.</span>
          <br/>
          <span className='beacon-label'><span className='beacon-header'>{listings.filter(l => l.Inventory_Status__c === 'Enabled').length.toLocaleString()}</span> with inventory enabled. <span className='beacon-header'>{listings.filter(l => l.Inventory_Status__c !== 'Enabled').length.toLocaleString()}</span> with inventory disabled.</span>
          <br/>
          <span className='beacon-label'><span className='beacon-header'>{listings.filter(l => l.Enable__c === false && l.Inventory_Status__c === 'Enabled').length.toLocaleString()}</span> have inventory enabled but are marketplace disabled.</span>
        </div>
      }
    </div>
  }, [accountIntegrations, selectedAccountIntegration, listings])

  // #region LOOKUPS CALLBACKS
  const loadListingInventory = async function (productId: string, mpn: string){
    changeStep('Loading Listing Product Inventories')
    changeSubStep(`Getting inventories for ${mpn}`)
    toggleLoading(true)
    const response = await apiFetch<SalesforceQueryResponse<ListingsInventory>>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: userData.accessToken,
      baseUrlOverride: props.environment === 'production' ? API_PROD_URL : API_DEV_URL,
      body: JSON.stringify({
        soql: getListingInventoryByProductId(productId)
      })
    })
    if(!response.ok){
      setError({
        closeError: () => {setError({visible: false})},
        content: JSON.stringify(response.body),
        description: `Failed to get listing inventories by product id.`,
        title: 'Listing Inventories Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    setInventoryModal({
      visible: true,
      inventories: response.body.records,
      title: `${mpn} Inventory`
    })
    toggleLoading(false)
  }

  const loadListingOrderItems = async function(productId: string, mpn: string){
    changeStep('Loading Listing Product Order Items')
    changeSubStep(`Getting order items for ${mpn}`)
    toggleLoading(true)
    const response = await apiFetch<SalesforceQueryResponse<ListingsOrderItem>>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: userData.accessToken,
      baseUrlOverride: props.environment === 'production' ? API_PROD_URL : API_DEV_URL,
      body: JSON.stringify({
        soql: getListingOrderItemsByProductId(productId)
      })
    })
    if(!response.ok){
      setError({
        closeError: () => {setError({visible: false})},
        content: JSON.stringify(response.body),
        description: `Failed to get listing order items by product id.`,
        title: 'Listing Order Items Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    setOrderItemsModal({
      visible: true,
      orderItems: response.body.records,
      title: `${mpn} Last 1000 Order Items`
    })
    toggleLoading(false)
  }

  const loadListingAdjustments = async function (productId: string, mpn: string){
    changeStep('Loading Listing Product Adjustments')
    changeSubStep(`Getting adjustments for ${mpn}`)
    toggleLoading(true)
    const response = await apiFetch<SalesforceQueryResponse<ListingAdjustment>>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: userData.accessToken,
      baseUrlOverride: props.environment === 'production' ? API_PROD_URL : API_DEV_URL,
      body: JSON.stringify({
        soql: getListingAdjustmentsByProductId(productId)
      })
    })
    if(!response.ok){
      setError({
        closeError: () => {setError({visible: false})},
        content: JSON.stringify(response.body),
        description: `Failed to get adjustments by product id.`,
        title: 'Adjustments Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    
    setAdjustmentsModal({
      adjustments: response.body.records,
      visible: true,
      title: `${mpn} Adjustments [${response.body.records.length}]`,
      mpn: mpn
    })
    toggleLoading(false)
  }

  const loadProductListings = async function(productId: string, mpn: string){
    changeStep('Loading Product Listings')
    changeSubStep(`Getting listings for ${mpn}`)
    toggleLoading(true)
    const response = await apiFetch<SalesforceQueryResponse<ListingAdjustment>>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: userData.accessToken,
      baseUrlOverride: props.environment === 'production' ? API_PROD_URL : API_DEV_URL,
      body: JSON.stringify({
        soql: getListingsByProductIdSOQL(productId)
      })
    })
    if(!response.ok){
      setError({
        closeError: () => {setError({visible: false})},
        content: JSON.stringify(response.body),
        description: `Failed to get listings by product id.`,
        title: 'Listings Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    //visible?: boolean, adjustments?: ListingAdjustment[], closeModal?: any, title?: string
    setListingsModal({
      listings: response.body.records,
      visible: true,
      title: `${mpn} Listings [${response.body.records.length}]`
    })
    toggleLoading(false)
  }

  const listingsTabs = (
    listings && (
      <Tabs>
        <TabList>
          <Tab>
            <span className='beacon-header'>
              <CalendarPlus size={25} /> CREATED [{listings.length.toLocaleString()}]
            </span>
          </Tab>
          <Tab>
            <span className='beacon-header'>
              <SlashCircle size={25} /> MISSING [{missing.length.toLocaleString()}]
            </span>
          </Tab>
          <Tab>
            <span className='beacon-header'>
              <Exclamation size={25} /> PENDING ENABLE [{pending.length.toLocaleString()}]
            </span>
          </Tab>
        </TabList>

        <TabPanel key='accountsCreatedTabPanel'>
          {<ListingsCreatedView 
            createdMarketingStatuses={createdMarketingStatuses}
            delistedReasons={delistedReasons}
            inventoryStatuses={inventoryStatuses}
            environment={props.environment}
            listings={listings}
            loadListingAdjustments={loadListingAdjustments}
            loadListingInventory={loadListingInventory}
            loadListingOrderItems={loadListingOrderItems}
            loadProductListings={loadProductListings}
            setError={setError}
            showListingsLookupButton={true}
          />}
        </TabPanel>

        <TabPanel key='accountsMissingTabPanel'>
          {<ListingsMissingView 
            delistedReasons={delistedReasons}
            environment={props.environment}
            inventoryStatuses={inventoryStatuses}
            loadListingAdjustments={loadListingAdjustments}
            loadListingInventory={loadListingInventory}
            loadListingOrderItems={loadListingOrderItems}
            loadProductListings={loadProductListings}
            missing={missing}
            missingMarketingStatuses={missingMarketingStatuses}
            setError={setError}
            showListingsLookupButton={true}
          />}
        </TabPanel>

        <TabPanel key='accountPendingTabPanel'>
          {pending && <ListingsPendingEnableView 
            delistedReasons={delistedReasons}
            environment={props.environment}
            inventoryStatuses={inventoryStatuses}
            loadListingAdjustments={loadListingAdjustments}
            loadListingInventory={loadListingInventory}
            loadListingOrderItems={loadListingOrderItems}
            loadProductListings={loadProductListings}
            pending={pending}
            setError={setError}
            showListingsLookupButton={true}
          />}
        </TabPanel>
      </Tabs>
    )
  );

  return <div>
      <ListingsInventoryModal 
        closeModal={() => { setInventoryModal({visible: false})} } 
        inventories={inventoryModal?.inventories}
        modalVisible={inventoryModal?.visible === true}
        title={inventoryModal?.title}
        environment={props.environment}
      />
      <ListingsOrderItemsModal 
        closeModal={() => { setOrderItemsModal({visible: false})} } 
        orderItems={orderItemsModal?.orderItems}
        modalVisible={orderItemsModal?.visible === true}
        title={orderItemsModal?.title}
        environment={props.environment}
        account={selectedAccountIntegration?.Name}
        accountIntegrations={accountIntegrations}
      />
      <ListingsAdjustmentsModal 
        closeModal={() => { setAdjustmentsModal({visible: false})} } 
        adjustments={adjustmentsModal?.adjustments}
        modalVisible={adjustmentsModal?.visible === true}
        title={adjustmentsModal?.title}
        environment={props.environment}
        productMpn={adjustmentsModal?.mpn}
      />
      <ProductListingsModal 
        closeModal={() => { setListingsModal({visible: false})} } 
        listings={listingsModal?.listings}
        modalVisible={listingsModal?.visible === true}
        title={listingsModal?.title}
        environment={props.environment}
        delistedReasons={delistedReasons}
        inventoryStatuses={inventoryStatuses}
      />
      <CustomError 
        closeError={() => {setError({visible: false})}}
        content={error?.content}
        description={error?.description}
        key={'all-listings-error'}
        title={error?.title}
        visible={error?.visible ?? false}
      />
      {accountIntegrations && <AccountsTable environment={props.environment} accounts={accountIntegrations} alwaysOpen={false} />}
      <br/>
      {accountsView}
      <br/>
      {listingsTabs}
    </div>
}